import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Dialog, Fab, Typography } from "@mui/material";
import Alert from "@mui/material/Alert";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import Loader from "components/Loader";
import QuestionAndAnswers from "components/QuestionAndAnswers";
import RedirectBackButton from "components/RedirectBackButton";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ResponseOfQuestion } from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import QuestionUpdateForm from "./QuestionUpdateForm";

function formatQuestionTitle(
  htmlOrText?: string,
  defaultValue?: string
): string | undefined {
  if (!htmlOrText) return defaultValue;

  let title: string;
  if (typeof window !== "undefined" && typeof document !== "undefined") {
    const div = document.createElement("div");
    div.innerHTML = htmlOrText;
    title = div.textContent?.trim() || htmlOrText.trim();
  } else {
    title = htmlOrText.trim();
  }

  return `${title.substring(0, 20)} - ${defaultValue}`;
}

const QuestionDetails = () => {
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { registerPromise } = useTrashablePromises();
  const { id } = useParams<{ id: string }>();
  const { questionsUrl, questionService } = useGlobalContext();
  const [editOpen, editOpenActions] = useBoolean(false);
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const { isLoading, isError, data, error } = useQuery<ResponseOfQuestion>(
    ["question", id],
    () => questionService.getQuestionById(id!),
    {
      enabled: !!id,
    }
  );

  const question = data?.data;
  const title = formatQuestionTitle(
    question?.value,
    t({ id: "questionLabel" })
  );
  useTitle(title);

  if (!id) return <Navigate to={questionsUrl()} replace />;
  if (isLoading) return <Loader />;
  if (isError) {
    console.log(error);

    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="questionLoadError" />
        </Alert>
      </Box>
    );
  }

  const onQuestionUpdated = () => {
    editOpenActions.setFalse();
    queryClient.invalidateQueries(["question", id]);
    queryClient.invalidateQueries("questions");

    setSnackbarMessage(new SnackbarMessageProps(t({ id: "questionUpdated" })));
  };

  const handleDelete = async () => {
    await registerPromise(questionService.disableQuestion(id));
    queryClient.invalidateQueries("questions");
    navigate(questionsUrl());
  };

  const questionDeleteInfo =
    question?.evaluationQuestionsCount! > 0
      ? "questionDeleteWithEvaluationInfo"
      : "questionDeleteInfo";

  return (
    <>
      <RedirectBackButton
        redirectRoute={questionsUrl()}
        labelKey="backToListButtonLabel"
      />
      <Box my={3} display="flex" justifyContent="center">
        <Fab
          aria-label="add"
          color="primary"
          onClick={editOpenActions.setTrue}
          data-cy="editButton"
        >
          <EditIcon />
        </Fab>
        <Box ml={4} />
        <Fab
          aria-label="add"
          color="secondary"
          onClick={deleteOpenActions.setTrue}
          data-cy="deleteButton"
        >
          <DeleteIcon />
        </Fab>
      </Box>
      <Typography variant="h4">
        <T id="questionLabel" />
      </Typography>

      <QuestionAndAnswers question={question} />

      <Box mb={3} textAlign="center"></Box>

      <Dialog open={editOpen} disableEnforceFocus maxWidth="lg" fullWidth>
        <QuestionUpdateForm
          question={question!}
          onQuestionUpdated={onQuestionUpdated}
          onCancel={editOpenActions.setFalse}
        />
      </Dialog>

      <DeleteEntityDialog
        dialogTitleKey="questionDeleteTitle"
        contentKey={questionDeleteInfo}
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default QuestionDetails;
